import { ApolloError } from 'apollo-client'
import { AxiosPromise } from 'axios'
import { PromiseErrorContextValue } from 'components/error'
import { format } from 'date-fns'
import { saveAs } from 'file-saver'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useServerTime } from 'hooks/useServerTime'
import { useCallback } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { previewFile } from 'util/previewFile'

type DownloadType = 'download' | 'preview'
const SUFFIXES = ['.pdf', '.csv']
export const reportFormatDate = 'yyyy-MM-dd_hhmm'

export interface HandleImpressaoPathProps<T> {
  apiPath: (values: T) => AxiosPromise<Blob | ArrayBuffer>
  values: T
  file: string
  downloadType?: DownloadType
  dateFormat?: string
  handleCatch?: PromiseErrorContextValue
  onSuccess?: () => void
}

export interface HandleImpressaoProps {
  blob: Blob
  file: string
  dateFormat?: string
}

const getBlobType = (fileName: string): object => {
  const extension = getExtension(fileName)
  if (extension === '.csv') return { type: 'text/csv' }
  return { type: 'application/pdf' }
}

const getExtension = (fileName: string): string | undefined => {
  const file = fileName.toLowerCase()
  return SUFFIXES.find((suffix) => file.endsWith(suffix))
}

const removeExtension = (fileName: string): string => fileName.substring(0, fileName.lastIndexOf('.'))

const addSufix = (fileName: string, suffix: string): string => {
  const fileExtension = getExtension(fileName)
  return fileExtension ? `${removeExtension(fileName)}${suffix}${fileExtension}` : `${fileName}${suffix}.pdf`
}

const addTreinamentoSuffix = (fileName: string, isTreinamento: boolean): string => {
  if (isTreinamento) return addSufix(fileName, '_treinamento')
  return fileName
}

const addDateTime = (fileName: string, date: Date, dateFormat: string): string =>
  addSufix(fileName, '_' + format(date, dateFormat))

export function useHandleImpressao() {
  const {
    configuracoes: { treinamento },
  } = useConfiguracoes()

  const { getServerTimeNow } = useServerTime()

  const handleImpressaoPath = useCallback(
    async <T>(props: Readonly<HandleImpressaoPathProps<T>>) => {
      const {
        apiPath,
        values,
        file,
        dateFormat,
        downloadType = 'preview',
        handleCatch = defaultHandleError,
        onSuccess,
      } = props

      try {
        const response = await apiPath(values)
        onSuccess?.()
        const type = getBlobType(file)
        const blob = new Blob([response.data], type)
        const filename = isUndefinedOrNull(dateFormat) ? file : addDateTime(file, getServerTimeNow(), dateFormat)
        if (downloadType === 'download') {
          saveAs(blob, addTreinamentoSuffix(filename, treinamento))
        } else {
          previewFile(blob, addTreinamentoSuffix(filename, treinamento))
        }
        return response
      } catch (error) {
        return handleCatch(error as ApolloError)
      }
    },
    [getServerTimeNow, treinamento]
  )

  const handleImpressao = useCallback(
    (props: Readonly<HandleImpressaoProps>) => {
      const { blob, file, dateFormat = reportFormatDate } = props
      const filename = isUndefinedOrNull(dateFormat) ? file : addDateTime(file, getServerTimeNow(), dateFormat)
      saveAs(blob, addTreinamentoSuffix(filename, treinamento))
    },
    [getServerTimeNow, treinamento]
  )

  return {
    handleImpressao,
    handleImpressaoPath,
  }
}

export const defaultHandleError = (error: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('loadend', (e) => {
      reject(new Error((e.target as any).result))
    })
    error.response && reader.readAsText(error.response.data)
  })
}
